import { useEffect, useState } from 'react';
import { Loader2, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { hardwareMaterialColors } from '@/lib/onshape-api';

const DEFAULT_COLOR = '#c0c4c8';

// "#AbC", "abc", "aabbcc" → "#aabbcc"; null if it isn't a hex color. Mirrors the backend's normalizeHexColor.
function normalizeHex(value) {
  let h = String(value || '').trim().replace(/^#/, '').toLowerCase();
  if (/^[0-9a-f]{3}$/.test(h)) h = h.split('').map((c) => c + c).join('');
  return /^[0-9a-f]{6}$/.test(h) ? `#${h}` : null;
}

/**
 * Settings card: the user's default color per material. Add Hardware applies the
 * saved color whenever the datasheet's material (or a hand-picked one) matches.
 * Saved per Onshape user in Firestore hardwarePrefs/{userId}.materialColors.
 */
export function MaterialColorsCard() {
  const [rows, setRows] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | ready | saving | saved
  const [error, setError] = useState('');

  useEffect(() => {
    hardwareMaterialColors()
      .then((res) => {
        setRows(res.materialColors || []);
        setMaterialOptions(res.materialOptions || []);
        setStatus('ready');
      })
      .catch((err) => {
        setError(err.message);
        setStatus('ready');
      });
  }, []);

  const setRow = (i, key, value) => {
    setRows((r) => r.map((row, j) => (j === i ? { ...row, [key]: value } : row)));
    setStatus('ready');
  };

  const invalid = rows.filter((r) => r.material.trim() || r.color.trim())
    .some((r) => !r.material.trim() || !normalizeHex(r.color));

  const save = async () => {
    setError('');
    setStatus('saving');
    try {
      const res = await hardwareMaterialColors(rows.filter((r) => r.material.trim()));
      setRows(res.materialColors || []);
      setStatus('saved');
    } catch (err) {
      setError(err.message);
      setStatus('ready');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Material colors</CardTitle>
        <CardDescription>
          Default appearance for each material when you add hardware. Overrides Otto&apos;s color guess.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {status === 'loading' ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </p>
        ) : (
          <>
            {rows.length === 0 && (
              <p className="text-sm text-muted-foreground">No material colors yet.</p>
            )}
            {rows.map((row, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto_auto] items-center gap-2">
                <Input
                  list="otto-settings-material-options"
                  value={row.material}
                  placeholder="Material"
                  onChange={(e) => setRow(i, 'material', e.target.value)}
                />
                <div className="flex gap-1">
                  <input
                    type="color"
                    value={normalizeHex(row.color) || DEFAULT_COLOR}
                    onChange={(e) => setRow(i, 'color', e.target.value)}
                    aria-label="Pick color"
                    className="h-9 w-12 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1"
                  />
                  <Input
                    value={row.color}
                    placeholder={DEFAULT_COLOR}
                    spellCheck={false}
                    aria-label="Hex color"
                    className={`w-24 font-mono ${row.color && !normalizeHex(row.color) ? 'border-destructive' : ''}`}
                    onChange={(e) => setRow(i, 'color', e.target.value.trim())}
                    onBlur={() => normalizeHex(row.color) && setRow(i, 'color', normalizeHex(row.color))}
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Remove material color"
                  onClick={() => {
                    setRows((r) => r.filter((_, j) => j !== i));
                    setStatus('ready');
                  }}
                >
                  <Trash2 />
                </Button>
              </div>
            ))}
            <datalist id="otto-settings-material-options">
              {materialOptions.map((m) => <option key={m} value={m} />)}
            </datalist>

            {error && <p className="text-sm text-destructive">{error}</p>}

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRows((r) => [...r, { material: '', color: '' }])}
              >
                <Plus /> Add material
              </Button>
              <Button size="sm" onClick={save} disabled={invalid || status === 'saving'}>
                {status === 'saving' ? <><Loader2 className="animate-spin" /> Saving…</> : 'Save'}
              </Button>
              {status === 'saved' && <span className="text-xs text-muted-foreground">Saved</span>}
              {invalid && (
                <span className="text-xs text-muted-foreground">Each row needs a material and a hex color.</span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
